import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Editor as TiptapEditor } from "@tiptap/react"

import { useHistoryStore } from "stores/historyStore"
import { findLabelLine } from "utils/findLabelLine"
import { useRestoreVersion, UseRestoreVersionProps } from "./useRestoreVersion"

/** What the editor hands its history panel */
export interface HistoryPanelBinding {
  isVisible: boolean
  onClose: () => void
  /** Restores a version; offered on Master only */
  onRestore?: (content: string) => Promise<void>
  /** Said in place of the restore button on a variant */
  restoreHint?: string
  /** Scrolls the editor to a question, by its label */
  onJumpToQuestion: (label: string) => void
  /** The version a `?history=<seq>` link opened the panel at */
  focusSeq: number | null
}

interface UseVersionHistoryReturn {
  isPanelOpen: boolean
  togglePanel: () => void
  panelProps: HistoryPanelBinding
}

// The open document as text, one line per paragraph: the lines the editor's scrollToLine counts
const editorText = (editor: TiptapEditor): string => {
  const lines: string[] = []
  editor.state.doc.forEach((node) => {
    lines.push(node.textContent)
  })
  return lines.join("\n")
}

/**
 * useVersionHistory wires the questionnaire editor to the project history panel: opening and
 * closing it, a `?history=<seq>` link (the Provenance timeline's "Open in editor"), restoring a
 * version (Master only) and jumping to a question a change names.
 */
export const useVersionHistory = (props: UseRestoreVersionProps): UseVersionHistoryReturn => {
  const { editor, editorRef, currentVariant } = props
  const { isPanelOpen, togglePanel, closePanel, openPanel } = useHistoryStore()

  // `?history=<seq>` opens the panel at that version. It is used once: the key leaves the URL as
  // the panel opens, so a later visit to the tab doesn't open the panel again.
  const [searchParams, setSearchParams] = useSearchParams()
  const historyLink = searchParams.get("history")
  const [focusSeq, setFocusSeq] = useState<number | null>(null)
  useEffect(() => {
    if (historyLink === null) return
    const seq = Number(historyLink)
    if (historyLink !== "" && Number.isInteger(seq) && seq >= 0) {
      setFocusSeq(seq)
      openPanel()
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("history")
        return next
      },
      { replace: true }
    )
  }, [historyLink, openPanel, setSearchParams])

  // Restore a version: the whole questionnaire, master and variants, through the editor's
  // load path. It is saved at once as a new version; the panel closes once it is in.
  const { restoreVersion } = useRestoreVersion(props)
  const restore = useCallback(
    async (content: string): Promise<void> => {
      if (await restoreVersion(content)) closePanel()
    },
    [restoreVersion, closePanel]
  )

  // Best effort: nothing happens when the open document doesn't declare the label
  const jumpToQuestion = useCallback(
    (label: string): void => {
      if (!editor || editor.isDestroyed) return
      const line = findLabelLine(editorText(editor), label)
      if (line !== null) editorRef.current?.scrollToLine(line)
    },
    [editor, editorRef]
  )

  // A restore puts the master in the editor, so it is offered on Master only
  const onMaster = currentVariant === ""
  return {
    isPanelOpen,
    togglePanel,
    panelProps: {
      isVisible: isPanelOpen,
      onClose: closePanel,
      onRestore: onMaster ? restore : undefined,
      restoreHint: onMaster ? undefined : "Switch to Master to restore a version",
      onJumpToQuestion: jumpToQuestion,
      focusSeq,
    },
  }
}
